
import React, {useEffect, useState} from 'react';
import TeacherLayout from "@/components/layout/teacher/teacherLayout";
import {Link, useParams} from "react-router-dom";
import {sprintf} from "@/lib/utils/sprintf";
import {URLS} from "@/lib/urls";
import {XCircleIcon} from "lucide-react";

import {useApiSearchTeacherStudents} from "@/api/userAcademy";
import {convertToJalaliWithFormat} from "@/lib/utils/date";
import PaginationRaw from "@/components/pagination/PaginationRaw";
import SearchInputRaw from "@/components/search/SearchInputRaw";
import {Button, Input} from "antd";
import RenderLoading from "@/components/render-if-loading";
import {CheckCircleOutlined, LoadingOutlined} from "@ant-design/icons";
import {useApiUpdateTeacherStudentScores} from "@/api/score";
import RenderIf from "@/components/render-if";


function TeacherStudentScorePage() {
    const {id} = useParams();

    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(15);

    const {data: students, isPending} = useApiSearchTeacherStudents({
        lesson_id: id,
        page: page,
        per_page: perPage,
        search: search
    });

    return (
        <TeacherLayout
            backTo={sprintf(URLS.teacher.lessons.detail, {id: id})}
            pageTitle={'ذخیره نمرات دانش‌ آموزان کلاس'}
        >
            <div className="mb-2">
                <div className="flex justify-between">
                    <SearchInputRaw setPage={setPage} setSearch={setSearch}/>
                </div>
            </div>

            <div className="rounded-2xl shadow-sm border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-900 ">
                <div className="divide-y divide-slate-100 dark:divide-gray-800">
                    <RenderLoading loading={isPending} skeleton={true}>
                        {students?.data?.data.map((student) => (
                            <div
                                key={student.id}
                                className="flex flex-col md:flex-row flex-wrap items-center rounded-lg gap-4 px-4 py-3.5 hover:bg-slate-50 dark:hover:bg-gray-800/70 cursor-pointer transition-colors mb-1"
                            >
                                <img
                                    src={student?.student_detail?.avatar ?? '/images/profile.jpg'}
                                    alt={student?.student_detail?.code}
                                    className="w-10 h-10 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-200 dark:ring-gray-700"
                                />

                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm text-slate-800 dark:text-gray-100">
                                        {student?.student_detail?.firstname}{' '}
                                        <span className="mx-1">{student?.student_detail?.lastname}</span>

                                        <span>
                                            <Link
                                                to={sprintf(URLS.teacher.lessons.studentsDetail, {
                                                    id: id,
                                                    studentId: student?.student
                                                })}
                                                className="text-xs text-slate-500 dark:text-gray-400"
                                            >
                                                <Button variant="link" type="link">
                                                    مشاهده پروفایل
                                                </Button>
                                            </Link>
                                        </span>
                                    </p>

                                    <div className="flex flex-wrap gap-2">
                                        <span className="text-xs text-slate-500 dark:text-gray-400">
                                            {student?.student_detail?.code}
                                        </span>

                                        <span className="text-xs text-slate-500 dark:text-gray-400">
                                            فرزند {student?.student_detail?.father}
                                        </span>

                                        <span className="text-xs text-slate-500 dark:text-gray-400">
                                            متولد {convertToJalaliWithFormat(student?.student_detail?.birthdate)}
                                        </span>

                                        <span className="text-xs text-slate-500 dark:text-gray-400 flex sm:hidden">
                                            موبایل {student?.student_detail?.mobile}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex flex-col lg:flex-row gap-4 items-center">
                                    <InputScoreComponent student={student} mode="score"/>
                                    <InputScoreComponent student={student} mode="desc"/>
                                </div>
                            </div>
                        ))}
                    </RenderLoading>
                </div>
            </div>

            <PaginationRaw data={students?.data} setPage={setPage} setPerPage={setPerPage}/>
        </TeacherLayout>
    );
}

export default TeacherStudentScorePage;


function InputScoreComponent({student, mode = 'score'}) {
    const [init, setInit] = useState(true);
    const [typing, setTyping] = useState(false);
    const [score, setScore] = useState(student[mode]);

    const {mutate: saveScore, isPending, isSuccess, isError} = useApiUpdateTeacherStudentScores();

    useEffect(() => {
        if (!init) {
            setTyping(true);

            const delaySaveScore = setTimeout(() => {
                let args = {};
                args[mode] = score;
                args['student_id'] = student?.id;

                saveScore(args, {
                    onSuccess: () => {}
                });

                setTyping(false);
            }, 1500);

            return () => clearTimeout(delaySaveScore);
        }
    }, [score]);

    let icon = <></>;

    if (isPending) {
        icon = <LoadingOutlined className="w-4 font-medium"/>;
    }

    if (isSuccess) {
        icon = <CheckCircleOutlined className="w-4 font-medium text-emerald-600 dark:text-emerald-400"/>;
    }

    if (isError) {
        icon = <XCircleIcon className="w-4 font-medium text-red-500 dark:text-red-400"/>;
    }

    if (typing) {
        icon = <div className="text-slate-500 dark:text-gray-400">...</div>;
    }

    return (
        <div className="flex flex-row flex-wrap items-center gap-2">
            <RenderIf condition={mode === 'score'}>
                <span className="text-slate-700 dark:text-gray-200">نمره</span>

                <Input
                    min={0}
                    onChange={(event) => {
                        setScore(event.target.value);
                        setInit(false);
                    }}
                    suffix={icon}
                    value={score}
                    step={0.01}
                    type="number"
                    size="large"
                    className="w-36 !bg-emerald-50 dark:!bg-emerald-950/40 !border-emerald-200 dark:!border-emerald-800 !text-emerald-700 dark:!text-emerald-300 font-medium"
                    placeholder="نمره"
                />
            </RenderIf>

            <RenderIf condition={mode === 'desc'}>
                <Input
                    min={0}
                    onChange={(event) => {
                        setScore(event.target.value);
                        setInit(false);
                    }}
                    suffix={icon}
                    value={score}
                    type="text"
                    size="large"
                    className="dark:!bg-gray-800 dark:!border-gray-600 dark:!text-gray-100 dark:placeholder:!text-gray-400"
                    placeholder="توضیحات یا جزئیات"
                />
            </RenderIf>
        </div>
    );
}
